import * as React from 'react';
import { escapeRegExp, isPlainObject } from 'lodash';
import isNil from 'lodash/isNil';

import { BASE64_IMAGE_PREFIX } from 'utils/content/constants';
import { replaceString } from 'utils/helper/common';

import type { HelperInstagramImageSize, LocationWindow } from 'types';

export const URL_REGEX = /((?:https?:\/\/|www\.)(?:(?:\([^\s()]+\))|[^\s()]+)+)/gi;
export const UNWANTED_LAST_CHAR_REGEX = /(.*)([.,?!])$/;

export function removeFinalSlash(url: string = ''): string {
  return url.substr(-1) === '/' ? url.substr(0, url.length - 1) : url;
}

export function stripQuery(url: string = ''): string {
  return url?.split('?')[0] || '';
}

export function isYoutubeUrl(url: string = ''): boolean {
  return /^(?:https?:\/\/)?(?:www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/.test(url);
}

export function isYoutubeHandleUrl(url: string = ''): boolean {
  return /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/@[\w-]+/.test(url);
}

export function isYoutubeImageCDNUrl(url: string = ''): boolean {
  return /^(https?:\/\/i.ytimg.com)/.test(url);
}

export function isYoutubeCDNUrl(url: string = ''): boolean {
  return /^(https?:\/\/img.youtube.com)/.test(url);
}

export function isTikTokVideoUrl(url: string = ''): boolean {
  return /^(https?:\/\/).*tiktok\.com\//.test(url);
}

export function isInstagramUrl(url: string = ''): boolean {
  // least strict check to include also images from Instagram CDN
  return /^((https?):\/\/)?(www.)?instagram\.com(\/[A-Za-z0-9_.]*)?\/p\/([a-zA-Z0-9_-]+)\/?/.test(url)
    || /instagram/.test(url); // new instagram CDN could be dynamic, but have instagram at the very beginning
}

export function isInstagramPostUrl(url: string = ''): boolean {
  // generic one to include both posts and reels, but slightly stricter than isInstagramUrl
  return /^((https?):\/\/)?(www.)?instagram\.com(\/[A-Za-z0-9_.]*)?\/p\/([a-zA-Z0-9_-]+)\/?/.test(url)
    || /^((https?):\/\/)?(www.)?instagram\./.test(url); // new instagram CDN could be dynamic, but have instagram at the very beginning
}

export function isStrictInstagramPostUrl(url: string = ''): boolean {
  return /^((https?):\/\/)?(www.)?instagram\.com(\/[A-Za-z0-9_.]*)?\/p\/([a-zA-Z0-9_-]+)\/?/.test(url);
}

export function isStrictInstagramReelUrl(url: string = ''): boolean {
  return /^((https?):\/\/)?(www.)?instagram\.com(\/[A-Za-z0-9_.]*)?\/reel\/([a-zA-Z0-9_-]+)\/?/.test(url);
}

export function isCampaignS3Url(url: string = ''): boolean {
  return /^(https?:\/\/s3\.eu-central-1\.amazonaws\.com\/(collabary-campaign-(images|assets|assets-staging)))/.test(url)
    || /^(https?:\/\/(collabary-campaign-(images|test|assets|assets-staging))\.s3\.eu-central-1\.amazonaws.com\/)/.test(url);
}

export function isValidOutfitS3VideoUrl(url: string): boolean {
  return /^(https?:\/\/(collabary-outfit-videos-(prod|test|staging))\.s3\.eu-central-1\.amazonaws\.com\/)/.test(url);
}

function isValidOutfitS3ThumbnailUrl(url: string): boolean {
  return /^(https?:\/\/(collabary-outfit-video-thumbnails-(prod|test|staging))\.s3\.eu-central-1\.amazonaws\.com\/)/.test(url);
}

export function isValidEntertainmentVideoS3Url(url: string): boolean {
  return /^(https?:\/\/(entertainment-video-media-(prod|test|staging))\.s3\.eu-central-1\.amazonaws\.com\/)/.test(url);
}

export function isValidEntertainmentVideoThumbnailS3Url(url: string): boolean {
  return /^(https?:\/\/(entertainment-video-thumbnails-(prod|test|staging))\.s3\.eu-central-1\.amazonaws\.com\/)/.test(url);
}

export function isS3Url(url: string = ''): boolean {
  return /^(https?:\/\/s3\.eu-central-1\.amazonaws\.com\/(sevsec-images-(prod|test|staging)|collabary-images-(prod|staging)))/.test(url)
    || /^(https?:\/\/s3\.eu-central-1\.amazonaws\.com\/(kiwi-assets|story(-staging)))/.test(url)
    || /^(https?:\/\/((sevsec-images-(prod|test|staging)|collabary-images-(prod|staging)))\.s3\.eu-central-1\.amazonaws\.com\/)/.test(url)
    || isValidOutfitS3VideoUrl(url)
    || isValidOutfitS3ThumbnailUrl(url)
    || isValidEntertainmentVideoS3Url(url)
    || isValidEntertainmentVideoThumbnailS3Url(url);
}

export function isCloudinaryUrl(url: string = ''): boolean {
  return /^(https?:\/\/res.cloudinary.com\/(collabary-test|collabary))/.test(url);
}

export function isZalandoCDN(url: string = ''): boolean {
  return /^https:\/\/[0-9a-z]+.ztat.net/.test(url);
}

export function isZalandoFashionStoreUrl(url: string = ''): boolean {
  return /https:\/\/(?:[a-zA-Z]{2}\.|www\.)?zalando\.(?:[a-zA-Z]{2,3}|com)(\/[^\s/].*)/.test(url);
}

export function isTiktokCDNUrl(url: string): boolean {
  const regexes = [
    /https:\/\/([0-9a-z.-])+.muscd.com\/.+/,
    /https:\/\/([0-9a-z.-])+.ibyteimg.com\/obj\/.+/,
    /https:\/\/([0-9a-z.-])+.akamaized.net\/obj\/([a-z0-9-]+)\/([a-z0-9])+/,
    /https:\/\/([0-9a-z.-])+.tiktokcdn.com(?:\/obj)?\/([a-z0-9-]+)\/([a-z0-9])+/
  ];

  return regexes.some(regex => regex.test(url));
}

export function isBase64Image(url: string): boolean {
  return url.startsWith(BASE64_IMAGE_PREFIX);
}

// https://techjira.zalando.net/browse/COLLAB-2875
export function isValidAssetUrl(url: string = ''): boolean {
  return isInstagramUrl(url)
    || isYoutubeUrl(url)
    || isYoutubeCDNUrl(url)
    || isYoutubeImageCDNUrl(url)
    || isS3Url(url)
    || isCampaignS3Url(url)
    || isCloudinaryUrl(url)
    || isZalandoCDN(url)
    || isTikTokVideoUrl(url)
    || isTiktokCDNUrl(url)
    || isBase64Image(url);
}

export function getExternalUrl(url?: string | null): string {
  if (!url) {
    return '';
  }
  const urlLowerCase = url.toLowerCase();
  return (urlLowerCase.startsWith('http') || urlLowerCase.startsWith('/')) ? url : `http://${url}`;
}

export function addProtocol(url: string | undefined | null = '', protocol: string = 'http'): string {
  if (!url) {
    return '';
  }

  const currentProtocol = url.split('://')[0];
  const hasProtocol = currentProtocol === 'http' || currentProtocol === 'https';
  const shouldAddProtocol = !hasProtocol;

  return shouldAddProtocol ? `${protocol}://${url}` : url;
}

function removeProtocol(url = ''): string {
  const parts = url.split('://');
  return parts[1] || parts[0];
}

export function cleanSocialHandle(url = ''): string {
  if (isYoutubeHandleUrl(url)) {
    return url;
  }
  if (isTikTokVideoUrl(url)) {
    return stripQuery(url);
  }
  return replaceString(stripQuery(url), '@', '');
}

function removeWWW(url = ''): string {
  return url.replace(/^www./, '');
}

export function removeProtocolAndWWW(url: string = ''): string {
  return removeWWW(removeProtocol(url));
}

function addWWW(url = ''): string {
  if (!url) {
    return url;
  }

  const parts = url.split('://');

  if (parts.length !== 2) {
    return url;
  }

  if (parts[1].startsWith('www.')) {
    return url;
  }

  return `${parts[0]}://www.${parts[1]}`;
}

export function normalizeTikTokUrl(url: string): string {
  const urlWithProtocol = addProtocol(stripQuery(url), 'https');

  if (url.includes('vm.tiktok.com')) {
    return urlWithProtocol;
  }

  return addWWW(urlWithProtocol);
}

export function normalizeInstagramUrl(url: string): string {
  return addWWW(addProtocol(stripQuery(url), 'https'));
}

export function normalizeYoutubeUrl(url: string): string {
  return addWWW(addProtocol(url, 'https'));
}

export function normalizeUrl(url: string): string {
  return addWWW(url);
}

export function getQueryValueFromUrl(location: LocationWindow, type: string): string | undefined | null {
  const query = location.query || {};
  return query[type];
}

const renderUrlAsLink = (textFragment, key): React.ReactElement<'a'> => (
  <a
    key={key}
    href={textFragment.toLowerCase().startsWith('http') ? textFragment : `http://${textFragment}`}
    rel="noopener noreferrer"
    target="_blank"
  >
    {textFragment}
  </a>
);

export function parseBasicUrls(text: string | undefined | null, key: string): Array<React.ReactElement<'a'> | string> {
  if (!text) {
    return [''];
  }

  const textFragments = text.split(URL_REGEX);

  // The URL_REGEX can include unwanted trailing chars, here they get moved out of the regex match
  const parsedTextFragments = [textFragments[0]];
  for (let i = 1; i < textFragments.length; i += 2) {
    const url = textFragments[i];
    if (url.match(UNWANTED_LAST_CHAR_REGEX)) {
      url.replace(UNWANTED_LAST_CHAR_REGEX, (match, p1: string, p2: string) => {
        parsedTextFragments.push(p1);
        parsedTextFragments.push(p2 + textFragments[i + 1]);
        return '';
      });
    } else {
      parsedTextFragments.push(textFragments[i]);
      parsedTextFragments.push(textFragments[i + 1]);
    }
  }

  // Render regex matches
  return parsedTextFragments.map((textFragment: string, index: number) => {
    if (index % 2) { // matched fragments are stored at uneven indices when splitting a string
      return renderUrlAsLink(textFragment, `${key}-${index}`);
    }

    return textFragment;
  });
}

// possible `size` values: 'l' (683x683), 'm' (320x320), 't' (150x150)
export function getInstagramImageByPostUrl(postUrl?: string | null, size?: HelperInstagramImageSize | null): string {
  if (!postUrl) {
    return '';
  }

  const url = postUrl.split('?')[0];
  const sizeStr = size ? `?size=${size}` : '';

  return getExternalUrl(`${url}${url.endsWith('/') ? '' : '/'}media/${sizeStr}`);
}

// possible `size` values: 'l' (683x683), 'm' (320x320), 't' (150x150)
export function getInstagramImageByPostId(postId: string, size: HelperInstagramImageSize): string {
  if (!postId) {
    return '';
  }

  const sizeStr = size ? `?size=${size}` : '';

  return `https://www.instagram.com/p/${postId}/media/${sizeStr}`;
}

// Helper functions that parse query objects to query strings and vice versa
// Example of query object: { foo: 'bar', parent: { type: 't', status: 's'} }
// Example of query string: `foo=bar&parent[type]=t&parent[status]=s`
export const Querystring = {
  stringToObject(input: string = '') {
    if (input.length === 0) {
      return {};
    }

    return input.split('&')
      .reduce((a, param) => {
        const regexResult = param.match(/^(.*)\[(.*)\]=(.*)$/);
        if (regexResult) {
          return {
            ...a,
            [regexResult[1]]: {
              ...a[regexResult[1]],
              [regexResult[2]]: decodeURI(regexResult[3])
            }
          };
        }

        if (param.includes('=')) {
          const [key, value] = param.split('=');
          return {
            ...a,
            [key]: decodeURI(value)
          };
        }

        return {};
      }, {});
  },
  objectToString(input: {
    [key: string]: any
  } = {}) {
    return Object.entries(input)
      .reduce<Array<string>>((a, [k, v]) => {
        if (typeof v === 'undefined') {
          // skip
          return a;
        }
        if (isPlainObject(v)) {
          a.push(...Object.entries(String(v)).map(([k2, v2]: [string, any]) => `${encodeURIComponent(k)}[${encodeURIComponent(k2)}]=${encodeURIComponent(String(v2))}`));
        } else {
          a.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
        }
        return a;
      }, [])
      .join('&');
  }
};

export const insertQueryIfPresent = (key: string, val?: string | null): string => (isNil(val) ? '' : `${key}=${val}`);

export const valueOrEmptyString = (val?: number | null): string | number => (isNil(val) ? '' : val);

export const getUrlParams = (): URLSearchParams => (
  new URLSearchParams(window.location.search)
);

export const getUrlParam = (key: string): string | null => (
  getUrlParams().get(key)
);

export const getHash = (identifier: string): number | null => (
  Number(window.location.hash.slice(1).replace(new RegExp(escapeRegExp(identifier), 'i'), '')) || null
);

export const isBlobUrl = (url: string): boolean => (
  url.startsWith('blob:')
);

export const isImageUrlByFileExtension = (
  url: string | undefined | null
): boolean => (
  /\.(jpg|jpeg|png|gif)/g.test(url || '')
);
